import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useToast } from '../components/Shared/Toast';
import { PLATFORM_ENVIRONMENTS } from '../constants/environments';

/**
 * ToolUrlAdminPage - einfache Edit-UI fuer die per-Env-URLs eines Tools (Tools-Admin).
 *
 * Pflegt je Tool aus der ToolRegistry:
 *   - base_url            (Fallback-URL)
 *   - metadata.urls[env]  (Ziel-URL je Umgebung: dev/staging/prod/demo)
 *
 * Diese Werte steuern im Dashboard den Tool-Link (ToolCard.js):
 *   metadata.urls[env]  ->  base_url  ->  https://{tool_id}.secaas.it
 *
 * Backend:
 *   GET /admin/tools              -> Registry inkl. base_url + metadata
 *   PUT /admin/tools/{tool_id}    -> base_url + metadata (urls) persistieren
 *
 * Mutation ist platform_admin-only (Backend 403 fuer org_admin) -> fuer andere
 * Rollen read-only.
 */
export default function ToolUrlAdminPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const { showToast } = useToast();
  const { userRole } = useAuth();

  const canManage = userRole === 'platform_admin';

  const [tools, setTools] = useState([]);
  const [drafts, setDrafts] = useState({});   // tool_id -> { base_url, urls: {env: url} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});   // tool_id -> bool

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/admin/tools');
      const list = Array.isArray(data) ? data : [];
      setTools(list);
      const d = {};
      list.forEach((tool) => {
        const urls = (tool.metadata && tool.metadata.urls) || {};
        d[tool.tool_id] = {
          base_url: tool.base_url || '',
          health_endpoint: tool.health_endpoint || '',
          modules: (Array.isArray(tool.available_modules) && tool.available_modules.length)
            ? [...tool.available_modules]
            : ['prod'],
          urls: PLATFORM_ENVIRONMENTS.reduce((acc, env) => {
            acc[env] = urls[env] || '';
            return acc;
          }, {}),
        };
      });
      setDrafts(d);
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => { load(); }, [load]);

  const setBaseUrl = (toolId, value) =>
    setDrafts((d) => ({ ...d, [toolId]: { ...d[toolId], base_url: value } }));

  const setHealthEndpoint = (toolId, value) =>
    setDrafts((d) => ({ ...d, [toolId]: { ...d[toolId], health_endpoint: value } }));

  const setEnvUrl = (toolId, env, value) =>
    setDrafts((d) => ({
      ...d,
      [toolId]: { ...d[toolId], urls: { ...d[toolId].urls, [env]: value } },
    }));

  const toggleModule = (toolId, env) =>
    setDrafts((d) => {
      const cur = d[toolId]?.modules || [];
      const next = cur.includes(env) ? cur.filter((e) => e !== env) : [...cur, env];
      // Kanonische Reihenfolge (dev/staging/prod/demo) beibehalten.
      const ordered = PLATFORM_ENVIRONMENTS.filter((e) => next.includes(e));
      return { ...d, [toolId]: { ...d[toolId], modules: ordered } };
    });

  const save = async (tool) => {
    const draft = drafts[tool.tool_id];
    if (!draft) return;
    setSaving((s) => ({ ...s, [tool.tool_id]: true }));
    try {
      const modules = (draft.modules && draft.modules.length) ? draft.modules : ['prod'];
      // Nur nicht-leere URLs der AUSGEWAEHLTEN Envs setzen; bestehende Metadaten erhalten.
      const urls = {};
      Object.entries(draft.urls).forEach(([env, url]) => {
        const v = (url || '').trim();
        if (v && modules.includes(env)) urls[env] = v;
      });
      const metadata = { ...(tool.metadata || {}), urls };
      await api(`/admin/tools/${tool.tool_id}`, {
        method: 'PUT',
        body: {
          base_url: draft.base_url.trim() || null,
          health_endpoint: (draft.health_endpoint || '').trim() || null,
          metadata,
          available_modules: modules,
        },
      });
      showToast(t('toolurls.saved', 'Gespeichert'), 'success');
      await load();
    } catch (err) {
      showToast(t('common.error', 'Fehler') + ': ' + (err?.response?.data?.detail || err?.message || ''), 'error');
    } finally {
      setSaving((s) => ({ ...s, [tool.tool_id]: false }));
    }
  };

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.loading', 'Lade...')}</div>;
  }

  return (
    <div style={{ maxWidth: 820 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
          {t('toolurls.title', 'Tool-URLs je Umgebung')}
        </h2>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          {t('toolurls.subtitle', 'Pro Tool die Ziel-URL je Umgebung (dev/staging/prod/demo) und die Basis-URL pflegen.')}
        </p>
      </div>

      {tools.length === 0 && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          {t('toolurls.empty', 'Keine Tools in der Registry.')}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {tools.map((tool) => {
          const draft = drafts[tool.tool_id] || { base_url: '', health_endpoint: '', urls: {} };
          return (
            <div
              key={tool.tool_id}
              style={{
                border: '1px solid var(--border)', borderRadius: 10, padding: 16,
                background: 'var(--bg-card)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {tool.label || tool.tool_id}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tool.tool_id}</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('toolurls.environments', 'Verfuegbare Umgebungen')}
                </span>
                {PLATFORM_ENVIRONMENTS.map((env) => (
                  <label key={env} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    <input
                      type="checkbox"
                      checked={(draft.modules || []).includes(env)}
                      onChange={() => toggleModule(tool.tool_id, env)}
                      disabled={!canManage}
                    />
                    {env}
                  </label>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px 10px', alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('toolurls.baseUrl', 'Basis-URL (Fallback)')}
                </label>
                <input
                  className="field-input"
                  type="text"
                  value={draft.base_url}
                  onChange={(e) => setBaseUrl(tool.tool_id, e.target.value)}
                  disabled={!canManage}
                  placeholder={`https://${tool.tool_id}.secaas.it`}
                />

                <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {t('toolurls.healthEndpoint', 'Health-URL (Monitoring)')}
                </label>
                <input
                  className="field-input"
                  type="text"
                  value={draft.health_endpoint || ''}
                  onChange={(e) => setHealthEndpoint(tool.tool_id, e.target.value)}
                  disabled={!canManage}
                  placeholder={`https://${tool.tool_id}.secaas.it/health`}
                />

                {PLATFORM_ENVIRONMENTS.filter((env) => (draft.modules || []).includes(env)).map((env) => (
                  <React.Fragment key={env}>
                    <label style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                      {env}
                    </label>
                    <input
                      className="field-input"
                      type="text"
                      value={draft.urls[env] || ''}
                      onChange={(e) => setEnvUrl(tool.tool_id, env, e.target.value)}
                      disabled={!canManage}
                      placeholder={`https://${tool.tool_id}-${env}.secaas.it`}
                    />
                  </React.Fragment>
                ))}
              </div>

              {canManage && (
                <div style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => save(tool)}
                    disabled={!!saving[tool.tool_id]}
                  >
                    {saving[tool.tool_id] ? t('toolurls.saving', 'Speichere...') : t('toolurls.save', 'Speichern')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{
        marginTop: 20, padding: '10px 14px',
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 8, fontSize: 12, color: 'var(--text-muted)',
      }}>
        {t('toolurls.hint', 'Leere Felder werden nicht gesetzt. Reihenfolge der Oeffnung: Umgebungs-URL, sonst Basis-URL, sonst https://{tool}.secaas.it. Die Health-URL wird (sofern gesetzt) unter Tool-Verbindungen als reine Ueberwachung (Monitor) geprueft - ohne Tenant-Sync.')}
      </div>
    </div>
  );
}
